//// state-restriction-modal.js


// State Restriction Modal Component
if (!customElements.get('state-restriction-modal')) {
  customElements.define(
    'state-restriction-modal',
    class StateRestrictionModal extends HTMLElement {
      constructor() {
        super();
        
        // Use event delegation for more reliable event handling
        this.addEventListener('click', this.handleClick.bind(this));
        this.addEventListener('keyup', this.onKeyUp.bind(this));
        
        // Flag to track if button events are bound
        this.buttonEventsBound = false;
      }

      handleClick(event) {
        console.log('Modal clicked:', event.target);
        
        // Handle close button clicks
        if (event.target.closest('.state-restriction-modal__close')) {
          console.log('Close button clicked');
          event.preventDefault();
          this.hide();
          return;
        }
        
        // Handle action button clicks
        if (event.target.closest('.state-restriction-modal__button')) {
          console.log('Action button clicked');
          event.preventDefault();
          this.hide();
          return;
        }
        
        // Handle backdrop clicks (clicking outside the content)
        if (event.target === this) {
          console.log('Backdrop clicked');
          this.hide();
        }
      }

      connectedCallback() {
        // Move modal to body to ensure proper z-index stacking
        if (this.moved) return;
        this.moved = true;
        document.body.appendChild(this);
      }

      show(region = '') {
        // Update the message with the region
        const messageElement = this.querySelector('.state-restriction-modal__message');
        if (messageElement && region) {
          messageElement.textContent = `Apologies, due to state law this product is not available for sale in ${region}.`;
        }

        // Show modal
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        
        // Focus management
        this.trapFocus();
        
        // Pause any playing media
        if (window.pauseAllMedia) {
          window.pauseAllMedia();
        }

        // Dispatch custom event
        this.dispatchEvent(new CustomEvent('modal:opened', { 
          bubbles: true, 
          detail: { modal: this } 
        }));
      }

      hide() {
        // Add closing animation class
        this.classList.add('closing');
        
        setTimeout(() => {
          document.body.classList.remove('overflow-hidden');
          this.removeAttribute('open');
          this.classList.remove('closing');
          
          // Restore focus
          this.restoreFocus();
          
          // Dispatch custom event
          this.dispatchEvent(new CustomEvent('modal:closed', { 
            bubbles: true, 
            detail: { modal: this } 
          }));
        }, 200); // Match CSS transition duration
      }

      onKeyUp(event) {
        // Close modal on Escape key
        if (event.code.toUpperCase() === 'ESCAPE') {
          this.hide();
        }
      }

      trapFocus() {
        const focusableElements = this.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Focus first element
        firstElement.focus();

        // Store original focus handler
        this.originalFocusHandler = this.handleFocusTrap.bind(this, firstElement, lastElement);
        
        // Add focus trap
        document.addEventListener('keydown', this.originalFocusHandler);
      }

      handleFocusTrap(firstElement, lastElement, event) {
        if (event.key !== 'Tab') return;

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }

      restoreFocus() {
        // Remove focus trap
        if (this.originalFocusHandler) {
          document.removeEventListener('keydown', this.originalFocusHandler);
          this.originalFocusHandler = null;
        }

        // Restore focus to trigger element if available
        if (this.triggerElement && this.triggerElement.focus) {
          this.triggerElement.focus();
        }
      }

      // Static method to create and show modal
      static show(region = '') {
        let modal = document.querySelector('state-restriction-modal');
        
        if (!modal) {
          // Create modal if it doesn't exist
          modal = StateRestrictionModal.createModal();
          document.body.appendChild(modal);
        }
        
        modal.show(region);
        return modal;
      }

      // Static method to create modal HTML
      static createModal() {
        const modal = document.createElement('state-restriction-modal');
        modal.className = 'state-restriction-modal';
        
        modal.innerHTML = `
          <div role="dialog" aria-modal="true" aria-labelledby="state-restriction-title" class="state-restriction-modal__content" tabindex="-1">
            <div class="state-restriction-modal__header">
              <h2 id="state-restriction-title" class="state-restriction-modal__title">Product Unavailable</h2>
              <button type="button" class="state-restriction-modal__close" aria-label="Close dialog">
                <svg class="icon" aria-hidden="true" focusable="false" viewBox="0 0 18 17">
                  <path d="m.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268-7.418 7.417Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
            <div class="state-restriction-modal__body">
              <div class="state-restriction-modal__icon">
                <svg class="icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24">
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,17A1.5,1.5 0 0,1 10.5,15.5A1.5,1.5 0 0,1 12,14A1.5,1.5 0 0,1 13.5,15.5A1.5,1.5 0 0,1 12,17M12,10A1,1 0 0,1 13,11V15A1,1 0 0,1 12,16A1,1 0 0,1 11,15V11A1,1 0 0,1 12,10Z" fill="currentColor"/>
                </svg>
              </div>
              <p class="state-restriction-modal__message">
                Apologies, due to state law this product is not available for sale in your region.
              </p>
              <div class="state-restriction-modal__actions">
                <button type="button" class="state-restriction-modal__button">
                  I Understand
                </button>
              </div>
            </div>
          </div>
        `;

        return modal;
      }
    }
  );
}

// Export for use in other scripts
window.StateRestrictionModal = customElements.get('state-restriction-modal');